import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePureH } from "../context/PureHContext";
import { Button, Card, TextInput } from "../components/lib";

/**
 * Iter 136 · Login page with Google Sign-In (invitation-only) + password
 * fallback. The password path stays live until Owner explicitly disables
 * it via env `PUREH_PASSWORD_FALLBACK=0`.
 */
export default function Login() {
  const { login } = usePureH();
  const navigate = useNavigate();
  const [email, setEmail] = useState("hassan@pureh.com");
  const [password, setPassword] = useState("••••••••");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await login(email, password);
      navigate("/pureh/dashboard", { replace: true });
    } catch (_e) {
      setErr("Login failed.");
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = () => {
    const origin = window.location.origin;
    const redirect = `${origin}/pureh/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="pureh-login-page">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded bg-slate-900 text-white flex items-center justify-center text-sm font-bold">P</div>
            <div className="text-left">
              <div className="text-lg font-semibold text-slate-900">Pure H OS</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Enterprise AI</div>
            </div>
          </div>
        </div>
        <Card>
          <div className="text-base font-semibold text-slate-900 mb-1">Sign in</div>
          <p className="text-xs text-slate-500 mb-5">
            Pure H OS is <b>invitation-only</b>. Continue with the Google account
            invited by your administrator, or use the emergency password path.
          </p>

          <button
            type="button"
            onClick={onGoogle}
            data-testid="login-google-btn"
            className="w-full flex items-center justify-center gap-2 border border-slate-300 rounded-md py-2 text-sm font-medium bg-white hover:bg-slate-50 mb-4"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.5 2.5 30.1 0 24 0 14.7 0 6.7 5.4 2.8 13.2l7.8 6.1C12.4 13.1 17.7 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.5 24.5c0-1.5-.1-2.9-.4-4.3H24v8.2h12.7c-.6 3.2-2.4 5.9-5.1 7.7l7.8 6.1c4.5-4.2 7.1-10.3 7.1-17.7z"/>
              <path fill="#FBBC05" d="M10.6 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.8-6.1C1 16.7 0 20.2 0 24s1 7.3 2.8 10.8l7.8-6.1z"/>
              <path fill="#34A853" d="M24 48c6.5 0 12-2.2 16-5.8l-7.8-6.1c-2.2 1.4-4.9 2.3-8.2 2.3-6.3 0-11.6-3.6-13.4-8.7l-7.8 6.1C6.7 42.6 14.7 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-slate-200" />
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Or password fallback</div>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">Email</label>
              <TextInput value={email} onChange={setEmail} testId="login-email" autoFocus />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">Password</label>
              <TextInput value={password} onChange={setPassword} type="password" testId="login-password" />
            </div>
            {err && <div className="text-xs text-red-600" data-testid="login-error">{err}</div>}
            <Button type="submit" onClick={onSubmit} disabled={busy} testId="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
